/**
 * Phase 07 chat — minimal SPA-side types.
 *
 * Mirrors the backend's `ChatThreadResponse` / `ChatMessageResponse`
 * shapes from vnext-api/vnext/schemas/chat.py. Wire field names stay
 * snake_case at the boundary; the transformers below convert to
 * camelCase for in-app use.
 */

import {
  ApiConversationAttachment,
  ConversationAttachment,
  transformToConversationAttachment,
} from './attachment';

export type ChatThreadKind = 'client_thread' | 'np_thread' | 'internal_thread';
export type ChatThreadEntityType = 'enquiry' | 'report' | 'report_request';
export type ChatMessageDirection = 'inbound' | 'outbound';
export type ChatMessageSenderKind = 'analyst' | 'client' | 'np' | 'system';

export interface ChatThread {
  id: string;
  /** Null for enquiry-anchored threads on an unclassified enquiry (no team
   *  assigned until triage) — matches the backend's nullable team_id. */
  teamId: string | null;
  kind: ChatThreadKind | string;
  entityType: ChatThreadEntityType | string;
  entityId: string;
  npId: string | null;
  freshdeskTicketId: number | null;
  lastMessageAt: string | null;
  lastInboundMessageAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ChatMessage {
  id: string;
  chatThreadId: string;
  direction: ChatMessageDirection | string;
  senderKind: ChatMessageSenderKind | string;
  senderUserId: string | null;
  senderEmail: string;
  senderDisplayName: string | null;
  bodyText: string;
  bodyHtml: string | null;
  isPrivate: boolean;
  freshdeskConversationId: number | null;
  deliveredAt: string | null;
  receivedAt: string | null;
  createdAt: string | null;
  // NOTE: use messageTimestamp() for display/grouping — createdAt is the
  // ROW INSERT time; backfilled history carries its real conversation
  // time on receivedAt/deliveredAt.
  /** Files bound to this message. The transformer always populates this
   *  (defaults to []); optional on the interface so older test fixtures
   *  and partial literals remain valid. Consumers should read
   *  `m.attachments ?? []`. */
  attachments?: ConversationAttachment[];
}

export interface ChatSendRequest {
  bodyText: string;
  isPrivate: boolean;
  /** Already-uploaded conversation attachments (this thread) to send with
   * the message — the backend forwards them to Freshdesk so the outbound
   * email carries the files. Analyst sends only. */
  attachmentIds?: string[];
}

interface ApiChatThread {
  id: string;
  team_id: string | null;
  kind: string;
  entity_type: string;
  entity_id: string;
  np_id: string | null;
  freshdesk_ticket_id: number | null;
  last_message_at: string | null;
  last_inbound_message_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ApiChatMessage {
  id: string;
  chat_thread_id: string;
  direction: string;
  sender_kind: string;
  sender_user_id: string | null;
  sender_email: string;
  sender_display_name: string | null;
  body_text: string;
  body_html: string | null;
  is_private: boolean;
  freshdesk_conversation_id: number | null;
  delivered_at: string | null;
  received_at: string | null;
  created_at: string | null;
  attachments?: ApiConversationAttachment[] | null;
}

export function transformToChatThread(api: ApiChatThread): ChatThread {
  return {
    id: api.id,
    teamId: api.team_id,
    kind: api.kind,
    entityType: api.entity_type,
    entityId: api.entity_id,
    npId: api.np_id,
    freshdeskTicketId: api.freshdesk_ticket_id,
    lastMessageAt: api.last_message_at,
    lastInboundMessageAt: api.last_inbound_message_at,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformToChatMessage(api: ApiChatMessage): ChatMessage {
  return {
    id: api.id,
    chatThreadId: api.chat_thread_id,
    direction: api.direction,
    senderKind: api.sender_kind,
    senderUserId: api.sender_user_id,
    senderEmail: api.sender_email,
    senderDisplayName: api.sender_display_name,
    bodyText: api.body_text,
    bodyHtml: api.body_html,
    isPrivate: api.is_private,
    freshdeskConversationId: api.freshdesk_conversation_id,
    deliveredAt: api.delivered_at,
    receivedAt: api.received_at,
    createdAt: api.created_at,
    attachments: (api.attachments ?? []).map(transformToConversationAttachment),
  };
}

/**
 * The message's effective time — when it was really received/sent.
 * Backfilled Freshdesk history stamps the original conversation time on
 * receivedAt/deliveredAt; createdAt (row insert) is only the fallback.
 */
export function messageTimestamp(message: {
  receivedAt: string | null;
  deliveredAt: string | null;
  createdAt: string | null;
}): string | null {
  return message.receivedAt ?? message.deliveredAt ?? message.createdAt;
}
